<template>
  <v-data-table
    :loading="loading"
    :headers="headers"
    :items="items"
    :sort-by.sync="sortBy"
    :sort-desc.sync="sortDesc"
    :footer-props="{
      'items-per-page-options': [10, 25, 50, 100]
    }"
    @click:row="rowClick"
    class="row-pointer"
  >
    <template v-slot:item.name="{ item }">
      <team-chip v-if="item.team" :team="item.team"></team-chip>
      <user-chip
        v-if="item.manager"
        :user="item.manager"
        is-manager
      ></user-chip>
      <user-chip v-if="item.user" :user="item.user"></user-chip>
      <span v-if="item.category">{{ item.category.name }}</span>
      <template v-if="item.question">
        <v-icon
          :color="getQuestionTypeColor(item.question.questionTypeId)"
          v-text="getQuestionTypeIcon(item.question.questionTypeId)"
        ></v-icon>
        {{ item.question.question }}
      </template>
    </template>
    <template v-slot:item.createdDate="{ item }">
      <v-icon color="secondary" class="mr-2">
        event
      </v-icon>
      {{ item.createdDate | longDate }}
    </template>
    <template v-slot:item.sentDate="{ item }">
      <v-icon color="secondary" class="mr-2">event</v-icon>
      {{ item.sentDate | longDate }}
    </template>
    <template v-slot:item.questions="{ item }">
      {{ item.userRecords.value.finished }} of {{ item.userRecords.value.sent }}
    </template>
    <template v-slot:item.finished="{ item }">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-bind="attrs"
            v-on="on"
            :color="
              item.finished == true
                ? 'success'
                : item.finished == null
                ? 'lighttext'
                : 'error'
            "
          >
            {{ item.finished ? "check_circle" : "cancel" }}
          </v-icon>
        </template>
        <div class="tooltip-limit-width">
          {{ completeTooltipText(item) }}
        </div>
      </v-tooltip>
    </template>
    <template v-slot:item.reviewer="{ item }">
      <user-chip :user="item.reviewer"></user-chip>
    </template>
    <template v-slot:item.averageDirectReports="{ item }">
      {{ item.averageDirectReports | one_decimal }}
    </template>
    <template v-slot:item.userRecords.value.finished="{ item }">
      {{ (item.userRecords.value || {}).finished || 0 }}
    </template>
    <template v-slot:item.userRecords.value.sent="{ item }">
      {{ (item.userRecords.value || {}).sent || 0 }}
    </template>
    <template v-slot:item.userRecords.value.completion="{ item }">
      <template
        v-if="
          item.userRecords &&
            item.userRecords.value &&
            filter.coreCardTypeId != $enums.coreCardTypes.PULSE &&
            coreCardType &&
            coreCardType.isPulse
        "
      >
        {{ item.userRecords.value.finished }} of
        {{ item.userRecords.value.sent }}
      </template>
      <simple-engagement-text
        :engagement="item.userRecords.value"
        v-else
      ></simple-engagement-text>
    </template>
    <template v-slot:item.userRecords.completion="{ item }">
      <template
        v-if="
          item.userRecords &&
            filter.coreCardTypeId != $enums.coreCardTypes.PULSE &&
            coreCardType &&
            coreCardType.isPulse
        "
      >
        {{ item.userRecords.finished }} of
        {{ item.userRecords.sent }}
      </template>
      <simple-engagement-text
        :engagement="item.userRecords"
        v-else
      ></simple-engagement-text>
    </template>
    <template v-slot:item.answer="{ item }">
      <v-icon v-if="!item.answer" color="lighttext">remove</v-icon>
      <template v-else>
        <v-icon small :color="$coreScores.getColor(item.answer)">
          fiber_manual_record
        </v-icon>
        <span class="secondarytext--text">
          {{ item.answer | no_decimal }}
        </span>
      </template>
    </template>
    <template v-slot:item.score.value="{ item }">
      <anonymous-icon v-if="isAnonymous(item)" small> </anonymous-icon>
      <v-icon
        v-else-if="
          !item.score || (item.score.value || defaultScore) == defaultScore
        "
        color="lighttext"
      >
        remove
      </v-icon>
      <template v-else-if="filter.coreCardTypeId == $enums.coreCardTypes.ENPS">
        <v-icon :color="$coreScores.getEnpsLabel(item.score.value).color">
          {{ $coreScores.getEnpsLabel(item.score.value).icon }}
        </v-icon>
        <span class="secondarytext--text">
          {{ item.score.value | no_decimal }}
        </span>
      </template>
      <v-row
        dense
        v-else-if="filter.coreCardTypeId == $enums.coreCardTypes.OVERALL"
      >
        <v-col cols="auto">
          <v-img
            :src="$coreScores.getOverallImage(item.score.value)"
            height="25"
            width="25"
            contain
          ></v-img>
        </v-col>
        <v-col class="my-auto">
          {{ item.score.value | no_decimal }}
        </v-col>
      </v-row>
      <template v-else>
        <v-icon small :color="$coreScores.getColor(item.score.value)">
          fiber_manual_record
        </v-icon>
        <span class="secondarytext--text">
          {{ item.score.value | one_decimal }}
        </span>
      </template>
    </template>
    <template v-slot:item.score.percentChange="{ item }">
      <anonymous-icon v-if="isAnonymous(item)" small> </anonymous-icon>
      <v-icon
        v-else-if="
          !item.score ||
            item.score.value == defaultScore ||
            item.score.compare == defaultScore
        "
        color="lighttext"
      >
        remove
      </v-icon>
      <compare-text
        v-else-if="item.score"
        :old-value="item.score.compare"
        :new-value="item.score.value"
        inherit
        :timeframeId="filter.timeframeId"
      ></compare-text>
    </template>
    <template v-slot:item.actions="{}">
      <v-btn icon v-if="$vuetify.breakpoint.mdAndUp">
        <v-icon>keyboard_arrow_right</v-icon>
      </v-btn>
    </template>
    <template v-slot:header.completed="{ header }">
      <v-badge
        :color="getGoalStatusTypeColor($enums.goalStatusTypes.COMPLETED)"
        left
        dot
        inline
      >
        {{ header.text }}
      </v-badge>
    </template>
    <template v-slot:header.onTrack="{ header }">
      <v-badge
        :color="getGoalStatusTypeColor($enums.goalStatusTypes.ON_TRACK)"
        left
        dot
        inline
      >
        {{ header.text }}
      </v-badge>
    </template>
    <template v-slot:header.offTrack="{ header }">
      <v-badge
        :color="getGoalStatusTypeColor($enums.goalStatusTypes.OFF_TRACK)"
        left
        dot
        inline
      >
        {{ header.text }}
      </v-badge>
    </template>
    <template v-slot:header.paused="{ header }">
      <v-badge
        :color="getGoalStatusTypeColor($enums.goalStatusTypes.PAUSED)"
        left
        dot
        inline
      >
        {{ header.text }}
      </v-badge>
    </template>
    <template v-slot:item.userRecords.percentChange="{ item }">
      <compare-text
        :old-value="
          (item.userRecords.compare || { completion: null }).completion
        "
        :new-value="(item.userRecords.value || { completion: null }).completion"
        inherit
        :timeframeId="filter.timeframeId"
      ></compare-text>
    </template>
    <template v-slot:item.resource.title="{ item }">
      <v-btn
        :href="item.resource.link"
        target="_blank"
        text
        color="secondary"
        class="px-0"
      >
        {{ item.resource.title }}
        <v-icon class="ml-2" small>launch</v-icon>
      </v-btn>
    </template>
    <template v-slot:item.resource.link="{ item }">
      <v-btn
        color="info"
        v-if="item.resource.link"
        :href="item.resource.link"
        target="_blank"
      >
        View
      </v-btn>
    </template>
    <template v-slot:item.survey.link="{ item }">
      <v-btn
        color="info"
        v-if="item.survey.link && filter.userId == profile.id"
        :href="item.survey.link"
        target="_blank"
      >
        Start
      </v-btn>
      <v-btn v-else-if="item.survey.isAnonymous" color="lighttext" outlined>
        Hidden
      </v-btn>
      <v-btn
        v-else-if="item.completed == true"
        class="light-background"
        color="info"
        text
        :to="
          `/reporting/surveys/overview/surveys/${item.survey.id}/sends/${item.survey.instanceId}/members/${filter.userId}`
        "
      >
        Results
      </v-btn>
    </template>
  </v-data-table>
</template>

<script>
import questionTypes from "@/helpers/mixins/questionTypes";
import goalStatusTypes from "@/helpers/mixins/goalStatusTypes";
import SimpleEngagementText from "@/components/reporting/SimpleEngagementText";
import CompareText from "@/components/reporting/CompareText";
import AnonymousIcon from "@/components/common/AnonymousIcon";
import { mapGetters } from "vuex";
export default {
  name: "ReportTable",
  props: {
    filter: { type: Object },
    headers: { type: Array },
    hideAnonymous: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    items: { type: Array },
    showChangeFilter: { type: Boolean, default: false }
  },
  components: {
    SimpleEngagementText,
    CompareText,
    AnonymousIcon
  },
  mixins: [goalStatusTypes, questionTypes],
  data: () => ({}),
  computed: {
    defaultScore() {
      return this.filter.coreCardTypeId == this.$enums.coreCardTypes.ENPS
        ? -100
        : 0;
    },
    coreCardType() {
      return this.settings.coreCardTypes.find(
        x => x.id == this.filter.coreCardTypeId
      );
    },
    sortBy: {
      get() {
        return this.$store.getters["reporting/sortBy"];
      },
      set(val) {
        this.$store.dispatch("reporting/setSortBy", val);
      }
    },
    sortDesc: {
      get() {
        return this.$store.getters["reporting/sortDesc"] || false;
      },
      set(val) {
        this.$store.dispatch("reporting/setSortDesc", val);
      }
    },
    completeTooltipText() {
      return item => {
        switch (this.filter.featureId) {
          case this.$enums.features.REVIEW:
            return item.finished == true
              ? "Has completed their reviews"
              : "Hasn't completed their reviews";
          default:
            return item.finished == true ? "Completed" : "Not Completed";
        }
      };
    },
    isAnonymous() {
      return item => {
        if (!this.hideAnonymous) {
          return false;
        }
        return (
          (item.team && item.team.hidePulseValues) ||
          (item.manager && item.manager.hidePulseValues) ||
          item.user
        );
      };
    },
    ...mapGetters({ profile: "profile/profile", settings: "profile/settings" })
  },
  methods: {
    rowClick(item) {
      this.$emit("rowClick", item);
    }
  }
};
</script>
